import { ArrowLeft } from "lucide-react";
import { useState, useCallback, useEffect } from "react";
import { ScrollRestoration, useNavigate, useParams } from "react-router-dom";
import ProductStructureCard from "./components/ProductStructureCard";
import {
  useGetGeneratedScriptsQuery,
  useGetMarketScriptQuery,
  useGetRecommendationQuery,
} from "@/redux/feature/product/productApi";
import ProductStructureCardSkeleton from "./components/ProductStructureCardSkeleton";
import MarketScriptSkeleton from "./components/MarketScriptSkeleton";
import { TProductStructureCard } from "./components/ProductStructureType";
import MarketScriptCard, {
  TMarketScriptCard,
} from "./components/MarketScriptCard";
import Modal from "@/components/ui/Modal";
import SelectModal from "./components/SelectModal/SelectModal";

const ProductStructure = () => {
  const navigate = useNavigate();
  const [selectedProductId, setSelectedProductId] = useState<string | null>(
    null
  );
  const [isSelectModalOpen, setIsSelectModalOpen] = useState(false);

  // Track script generation states per product
  const [scriptGenerationStates, setScriptGenerationStates] = useState<
    Record<
      string,
      {
        isGenerating: boolean;
        isReady: boolean;
        hasError: boolean;
        scripts: any[];
      }
    >
  >({});

  const { brandName, productName, persona, country, productId } = useParams();
  const [currentlyPlaying, setCurrentlyPlaying] = useState<string | null>(null);

  const { data: recommendationsData } = useGetRecommendationQuery({
    productId: productId as string,
    persona: persona as string,
  });

  const {
    data: generatedScript,
    isError: scriptError,
    isLoading: scriptLoading,
  } = useGetGeneratedScriptsQuery(
    {
      brand: brandName as string,
      product: productName as string,
      productId: productId as string,
      id: selectedProductId as string,
      selected_persona: persona as string,
      country: country as string,
    },
    {
      skip: !selectedProductId,
      pollingInterval: 30000,
    }
  );

  // Update script generation states when data changes
  useEffect(() => {
    if (selectedProductId && generatedScript) {
      const isGenerating = generatedScript?.meta?.status === "pending";
      const isCompleted = generatedScript?.meta?.status === "completed";

      setScriptGenerationStates((prev) => ({
        ...prev,
        [selectedProductId]: {
          isGenerating: isGenerating,
          isReady: isCompleted,
          hasError: scriptError || false,
          scripts: isCompleted
            ? generatedScript?.data || []
            : prev[selectedProductId]?.scripts || [],
        },
      }));
    }
  }, [selectedProductId, generatedScript, scriptError]);

  // Check if any product is currently generating a script
  const isAnyScriptGenerating = Object.values(scriptGenerationStates).some(
    (state) => state.isGenerating
  );

  // Handle script generation initiation
  const handleSetSelectedProductId = (productId: string) => {
    // Prevent starting new generation if any script is already generating
    if (isAnyScriptGenerating) {
      return;
    }

    setSelectedProductId(productId);

    // Initialize or update the state for this product
    setScriptGenerationStates((prev) => ({
      ...prev,
      [productId]: {
        isGenerating: true,
        isReady: false,
        hasError: false,
        scripts: prev[productId]?.scripts || [],
      },
    }));
  };

  const {
    data: marketScript,
    refetch: refetchMarketScript,
    isFetching: isFetchingMarketScript,
  } = useGetMarketScriptQuery(
    {
      productId: productId as string,
      selected_persona: persona as string,
    },
    {
      // Initial fetch with no polling
      pollingInterval: 0,
    }
  );

  // Polling effect based on isProcessing status
  useEffect(() => {
    let intervalId: NodeJS.Timeout;

    if (marketScript?.meta?.isProcessing) {
      // If still processing, start polling every 3 seconds
      intervalId = setInterval(() => {
        refetchMarketScript();
      }, 3000);
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [marketScript?.meta?.isProcessing, refetchMarketScript]);

  // Market script status
  const isMarketScriptProcessing = marketScript?.meta?.isProcessing === true;
  const isMarketScriptCompleted = marketScript?.meta?.status === "completed";
  const shouldShowMarketScriptBanner =
    isMarketScriptProcessing || isFetchingMarketScript;
  const shouldShowMarketScriptCards =
    isMarketScriptCompleted && marketScript?.data?.length > 0;

  const handleGoBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  // Helper function to get script state for a specific product
  const getScriptStateForProduct = (productId: string) => {
    const state = scriptGenerationStates[productId];
    return {
      isGenerating: state?.isGenerating || false,
      isReady: state?.isReady || false,
      hasError: state?.hasError || false,
      scripts: state?.scripts || [],
    };
  };

  return (
    <div>
      {/* Back Button */}
      <button
        onClick={handleGoBack}
        className="flex gap-x-1.5 items-center text-base font-semibold"
      >
        <ArrowLeft size={15} /> Back to Collection
      </button>

      {/* Header */}
      <div className="mb-8 mt-9">
        <h1 className="text-gray-900 text-2xl sm:text-2xl font-semibold">
          Recommended Structures
        </h1>
        <p className="text-gray-500 text-sm mt-1">
          Select a video structure to enhance the Content
        </p>
      </div>

      {/* Error Message - Only show for the selected product */}
      {selectedProductId &&
        scriptGenerationStates[selectedProductId]?.hasError && (
          <div className="mt-6 p-4 bg-[#FEF2F2] rounded-lg border border-[#FCA5A5] mb-10">
            <p className="text-sm text-[#DC2626]">
              Failed to generate script. Please try selecting another structure.
            </p>
          </div>
        )}

      {/* generating marketing script banner */}
      {shouldShowMarketScriptBanner && (
        <div className="mt-6 p-4 bg-[#F0F9FF] rounded-lg border border-[#7DD3FC] mb-10">
          <div className="flex flex-col md:flex-row items-center gap-3">
            <div className="animate-spin rounded-full h-5 w-5 border-2 border-[#0EA5E9] border-t-transparent" />
            <div>
              <p className="text-sm text-[#0EA5E9] font-semibold">
                Generating your marketing script...
              </p>
              <p className="text-xs text-[#0369A1] mt-1">
                Our AI is crafting a compelling script tailored to your selected
                structure. This may take a few moments.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Cards */}
      <section className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-[42px] flex-wrap items-center">
        {!recommendationsData || !recommendationsData?.data
          ? Array.from({ length: 3 }).map((_, index) => (
              <ProductStructureCardSkeleton key={index} />
            ))
          : recommendationsData?.data?.map(
              (item: TProductStructureCard, index: number) => {
                const scriptState = getScriptStateForProduct(item.id);

                return (
                  <ProductStructureCard
                    key={item.id}
                    data={item}
                    index={index + 1}
                    setIsSelectModalOpen={setIsSelectModalOpen}
                    currentlyPlaying={currentlyPlaying}
                    setCurrentlyPlaying={setCurrentlyPlaying}
                    setSelectedProductId={handleSetSelectedProductId}
                    isSelected={selectedProductId === item.id}
                    isGeneratingScript={scriptState.isGenerating}
                    isScriptReady={scriptState.isReady}
                    isError={scriptState.hasError}
                    generatedScripts={scriptState.scripts}
                    isAnyScriptGenerating={isAnyScriptGenerating}
                    shouldShowMarketScriptBanner={shouldShowMarketScriptBanner}
                  />
                );
              }
            )}

        {/* Market Script Cards skeleton*/}
        {shouldShowMarketScriptBanner && <MarketScriptSkeleton />}

        {/* Market Script Cards */}
        {shouldShowMarketScriptCards &&
          marketScript?.data?.map(
            (script: TMarketScriptCard, index: number) => (
              <MarketScriptCard
                index={(recommendationsData?.data?.length || 0) + 1 + index}
                key={`market-${index}`}
                data={script}
              />
            )
          )}
      </section>

      <Modal
        isOpen={isSelectModalOpen}
        setOpen={setIsSelectModalOpen}
        width="min(900px, 95vw)"
      >
        <SelectModal
          isOpen={isSelectModalOpen}
          setOpen={setIsSelectModalOpen}
        />
      </Modal>
      <ScrollRestoration />
    </div>
  );
};

export default ProductStructure;
